import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

import { SignalRService } from './singal-r.service';
import { OnboardingService } from './api/onboarding.service';
import { selectToken } from '../../store/selectors/token.selectors';

@Injectable({ providedIn: 'root' })
export class AppInitializationService implements OnDestroy {
  readonly initializing$ = new BehaviorSubject<boolean>(false);

  private _initialized = false;
  private _initPromise: Promise<void> | null = null;
  private _currentToken = '';
  private _tokenSub: Subscription | null = null;

  constructor(
    private readonly store: Store,
    private readonly signalRService: SignalRService,
    private readonly onboardingService: OnboardingService,
  ) {}

  get initialized(): boolean {
    return this._initialized;
  }

  ensureInitialized(): Promise<void> {
    if (this._initialized) return Promise.resolve();
    if (this._initPromise) return this._initPromise;

    this._initPromise = this._runInitialization();
    return this._initPromise;
  }

  reset(): void {
    this._initialized = false;
    this._initPromise = null;
    this._tokenSub?.unsubscribe();
    this._tokenSub = null;
    this.signalRService.stopConnection();
    this.initializing$.next(false);
  }

  ngOnDestroy(): void {
    this._tokenSub?.unsubscribe();
  }

  private async _runInitialization(): Promise<void> {
    this.initializing$.next(true);

    try {
      this._subscribeToToken();

      await firstValueFrom(
        this.store.select(selectToken).pipe(
          filter((t): t is string => !!t),
          take(1),
        ),
      );

      await Promise.all([
        this._startSignalR(),
        this._prefetchCountries(),
      ]);

      this._initialized = true;
    } catch (err) {
      console.error('[AppInit] Initialization failed:', err);
      this._initialized = true;
    } finally {
      this.initializing$.next(false);
    }
  }

  private _subscribeToToken(): void {
    if (this._tokenSub) return;

    this._tokenSub = this.store
      .select(selectToken)
      .pipe(
        filter((t): t is string => !!t),
        distinctUntilChanged(),
      )
      .subscribe((token) => {
        this._currentToken = token;
      });
  }

  private async _startSignalR(): Promise<void> {
    await this.signalRService.startConnection(() => this._currentToken);
  }

  private _prefetchCountries(): Promise<void> {
    return firstValueFrom(this.onboardingService.getCountries()).then(
      () => {},
    );
  }
}
